import { formatTickMark } from "./lightweightChartUtils";

export const VOLUME_CANDLE_VIEW = "volume-candles";
export const VOLUME_CANDLE_REFERENCE_BARS = 100;
export const VOLUME_CANDLE_REFERENCE_PERCENTILE = 0.95;
// Keep low-volume bodies readable without compressing valid ratios above this floor.
export const VOLUME_CANDLE_MIN_WIDTH_RATIO = 0.14;
export const VOLUME_CANDLE_GAP_PX = 3;
export const VOLUME_CANDLE_MIN_BODY_WIDTH_PX = 0.8;
export const VOLUME_CANDLE_FALLBACK_WIDTH_RATIO = 0.72;

const finitePositiveVolume = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

export const percentileValue = (values, percentile) => {
  const sorted = (Array.isArray(values) ? values : [])
    .map(finitePositiveVolume)
    .filter((value) => value != null)
    .sort((left, right) => left - right);
  if (sorted.length === 0) return null;

  const boundedPercentile = Math.min(Math.max(Number(percentile) || 0, 0), 1);
  const rank = boundedPercentile * (sorted.length - 1);
  const lowerIndex = Math.floor(rank);
  const upperIndex = Math.ceil(rank);
  if (lowerIndex === upperIndex) return sorted[lowerIndex];

  const weight = rank - lowerIndex;
  return sorted[lowerIndex] + ((sorted[upperIndex] - sorted[lowerIndex]) * weight);
};

export const volumeCandleReference = (
  candleData,
  referenceBars = VOLUME_CANDLE_REFERENCE_BARS,
) => {
  const candles = Array.isArray(candleData) ? candleData : [];
  if (candles.length <= 1) return null;

  const completed = candles.slice(
    Math.max(0, candles.length - referenceBars - 1),
    candles.length - 1,
  );
  return percentileValue(
    completed.map((candle) => candle?.volume),
    VOLUME_CANDLE_REFERENCE_PERCENTILE,
  );
};

export const volumeCandleWidthRatio = ({ volume, referenceVolume }) => {
  const normalizedVolume = finitePositiveVolume(volume);
  const normalizedReference = finitePositiveVolume(referenceVolume);
  if (normalizedVolume == null || normalizedReference == null) {
    return VOLUME_CANDLE_FALLBACK_WIDTH_RATIO;
  }

  return Math.max(
    normalizedVolume / normalizedReference,
    VOLUME_CANDLE_MIN_WIDTH_RATIO,
  );
};

const normalizedPixelRatio = (value) => {
  const numeric = Math.abs(Number(value));
  return Number.isFinite(numeric) && numeric > 0 ? numeric : 1;
};

const snapToDevicePixel = (value, pixelRatio) => (
  Math.round(Number(value) * pixelRatio) / pixelRatio
);

// Lightweight Charts snaps its native candles to backing-store pixels. Keep
// the custom packed overlay on that same grid so Canvas does not antialias the
// body edges, wicks, or the fixed gap between adjacent candles.
export const alignVolumeCandleLayoutToDevicePixels = ({
  layout,
  pixelRatio,
}) => {
  const items = Array.isArray(layout?.items) ? layout.items : [];
  const ratio = normalizedPixelRatio(pixelRatio);
  if (items.length === 0) {
    return {
      ...(layout || {}),
      items: [],
      pixelRatio: ratio,
    };
  }

  const requestedGap = Math.max(Number(layout.gapPx) || 0, 0);
  const gapDevicePixels = requestedGap > 0
    ? Math.max(Math.round(requestedGap * ratio), 1)
    : 0;
  const alignedGap = gapDevicePixels / ratio;
  let cursorDevicePixels = Math.round((Number(items[0].left) || 0) * ratio);
  let carriedWidthError = 0;
  const alignedItems = items.map((item) => {
    const requestedWidthDevicePixels = Math.max(
      (Number(item.width) || 0) * ratio,
      0,
    );
    const adjustedWidth = requestedWidthDevicePixels + carriedWidthError;
    const widthDevicePixels = Math.max(Math.round(adjustedWidth), 1);
    carriedWidthError = adjustedWidth - widthDevicePixels;

    const leftDevicePixels = cursorDevicePixels;
    const rightDevicePixels = leftDevicePixels + widthDevicePixels;
    cursorDevicePixels = rightDevicePixels + gapDevicePixels;
    const left = leftDevicePixels / ratio;
    const right = rightDevicePixels / ratio;
    return {
      ...item,
      x: (left + right) / 2,
      left,
      right,
      width: widthDevicePixels / ratio,
    };
  });

  return {
    ...layout,
    items: alignedItems,
    gapPx: alignedGap,
    left: alignedItems[0].left,
    right: alignedItems[alignedItems.length - 1].right,
    pixelRatio: ratio,
  };
};

export const alignVolumeCandleTranslationToDevicePixels = ({
  offset,
  pixelRatio,
}) => {
  const ratio = normalizedPixelRatio(pixelRatio);
  return snapToDevicePixel(Number(offset) || 0, ratio);
};

export const alignVolumeCandleRectToDevicePixels = ({
  left,
  right,
  top,
  bottom,
  pixelRatioX,
  pixelRatioY,
}) => {
  const ratioX = normalizedPixelRatio(pixelRatioX);
  const ratioY = normalizedPixelRatio(pixelRatioY ?? pixelRatioX);
  const alignedLeft = snapToDevicePixel(left, ratioX);
  const alignedRight = Math.max(
    snapToDevicePixel(right, ratioX),
    alignedLeft + (1 / ratioX),
  );
  const alignedTop = snapToDevicePixel(Math.min(top, bottom), ratioY);
  const alignedBottom = Math.max(
    snapToDevicePixel(Math.max(top, bottom), ratioY),
    alignedTop + (1 / ratioY),
  );
  return {
    left: alignedLeft,
    top: alignedTop,
    width: alignedRight - alignedLeft,
    height: alignedBottom - alignedTop,
  };
};

export const alignVolumeCandleWickToDevicePixels = ({
  x,
  top,
  bottom,
  lineWidth = 1,
  pixelRatioX,
  pixelRatioY,
}) => {
  const ratioX = normalizedPixelRatio(pixelRatioX);
  const ratioY = normalizedPixelRatio(pixelRatioY ?? pixelRatioX);
  const widthDevicePixels = Math.max(
    Math.round(Math.max(Number(lineWidth) || 0, 0) * ratioX),
    1,
  );
  const centerDevicePixels = (Number(x) || 0) * ratioX;
  const leftDevicePixels = Math.round(
    centerDevicePixels - (widthDevicePixels / 2),
  );
  const topDevicePixels = Math.round(Math.min(top, bottom) * ratioY);
  const bottomDevicePixels = Math.max(
    Math.round(Math.max(top, bottom) * ratioY),
    topDevicePixels + 1,
  );
  return {
    left: leftDevicePixels / ratioX,
    top: topDevicePixels / ratioY,
    width: widthDevicePixels / ratioX,
    height: (bottomDevicePixels - topDevicePixels) / ratioY,
  };
};

const allocatePackedWidths = ({ weights, bodySpan, minimumWidth }) => {
  const widths = new Array(weights.length).fill(0);
  if (weights.length === 0 || bodySpan <= 0) return widths;

  const floor = Math.min(
    Math.max(Number(minimumWidth) || 0, 0),
    bodySpan / weights.length,
  );
  let remainingSpace = bodySpan;
  let active = weights.map((_weight, index) => index);

  while (active.length > 0) {
    const totalWeight = active.reduce(
      (sum, index) => sum + Math.max(weights[index], 0),
      0,
    );
    const proportionalWidth = (index) => (
      totalWeight > 0
        ? remainingSpace * (Math.max(weights[index], 0) / totalWeight)
        : remainingSpace / active.length
    );
    const belowFloor = active.filter(
      (index) => proportionalWidth(index) < floor,
    );

    if (belowFloor.length === 0) {
      active.forEach((index) => {
        widths[index] = proportionalWidth(index);
      });
      break;
    }

    belowFloor.forEach((index) => {
      widths[index] = floor;
    });
    remainingSpace = Math.max(
      remainingSpace - (floor * belowFloor.length),
      0,
    );
    const belowFloorSet = new Set(belowFloor);
    active = active.filter((index) => !belowFloorSet.has(index));
  }

  return widths;
};

export const buildPackedVolumeCandleGeometry = ({
  candles,
  left,
  right,
  referenceVolume,
  gapPx = VOLUME_CANDLE_GAP_PX,
  minimumWidth = VOLUME_CANDLE_MIN_BODY_WIDTH_PX,
}) => {
  const entries = Array.isArray(candles)
    ? candles.filter((entry) => entry?.candle)
    : [];
  const start = Number(left);
  const end = Number(right);
  if (
    entries.length === 0 ||
    !Number.isFinite(start) ||
    !Number.isFinite(end) ||
    end <= start
  ) {
    return {
      items: [],
      gapPx: 0,
      left: Number.isFinite(start) ? start : 0,
      right: Number.isFinite(end) ? end : 0,
    };
  }

  const span = end - start;
  const minimumBodyWidth = Math.max(Number(minimumWidth) || 0, 0);
  const requestedGap = Math.max(Number(gapPx) || 0, 0);
  const maxGap = entries.length > 1
    ? Math.max(
        (span - (minimumBodyWidth * entries.length)) /
          (entries.length - 1),
        0,
      )
    : 0;
  const resolvedGap = entries.length > 1
    ? Math.min(requestedGap, maxGap)
    : 0;
  const bodySpan = Math.max(
    span - (resolvedGap * Math.max(entries.length - 1, 0)),
    0,
  );
  const weights = entries.map((entry) => volumeCandleWidthRatio({
    volume: entry.candle?.volume,
    referenceVolume,
  }));
  const widths = allocatePackedWidths({
    weights,
    bodySpan,
    minimumWidth: minimumBodyWidth,
  });

  let cursor = start;
  const items = entries.map((entry, index) => {
    const bodyWidth = widths[index];
    const bodyLeft = cursor;
    const bodyRight = bodyLeft + bodyWidth;
    cursor = bodyRight + resolvedGap;
    return {
      ...entry,
      x: bodyLeft + (bodyWidth / 2),
      left: bodyLeft,
      right: bodyRight,
      width: bodyWidth,
    };
  });

  return {
    items,
    gapPx: resolvedGap,
    left: start,
    right: end,
  };
};

const widthScaleFromAllocation = ({ weights, widths, minimumWidth }) => {
  const scalableIndexes = widths
    .map((width, index) => ({ width, index }))
    .filter(({ width, index }) => (
      width > minimumWidth + 0.000001 && weights[index] > 0
    ));
  const samples = scalableIndexes.length > 0
    ? scalableIndexes
    : widths
      .map((width, index) => ({ width, index }))
      .filter(({ index }) => weights[index] > 0);
  const allocated = samples.reduce((sum, sample) => sum + sample.width, 0);
  const weight = samples.reduce(
    (sum, sample) => sum + weights[sample.index],
    0,
  );
  return weight > 0 ? allocated / weight : 0;
};

export const buildStableVolumeCandleGeometry = ({
  candles,
  barSpacing,
  referenceVolume,
  geometryScale: providedGeometryScale,
  gapPx = VOLUME_CANDLE_GAP_PX,
  minimumWidth = VOLUME_CANDLE_MIN_BODY_WIDTH_PX,
}) => {
  const entries = Array.isArray(candles)
    ? candles.filter((entry) => entry?.candle)
    : [];
  const spacing = Math.max(Number(barSpacing) || 0, 0);
  if (entries.length === 0 || spacing <= 0) {
    return {
      items: [],
      gapPx: 0,
      left: 0,
      right: 0,
      barSpacing: spacing,
      geometryScale: 0,
    };
  }

  const floor = Math.max(Number(minimumWidth) || 0, 0);
  const resolvedGap = Math.min(
    Math.max(Number(gapPx) || 0, 0),
    Math.max(spacing - floor, 0),
  );
  const targetBodyWidth = Math.max(spacing - resolvedGap, floor);
  const weights = entries.map((entry) => volumeCandleWidthRatio({
    volume: entry.candle?.volume,
    referenceVolume,
  }));
  const requestedGeometryScale = Number(providedGeometryScale);
  let geometryScale = requestedGeometryScale;
  let widths;
  if (Number.isFinite(requestedGeometryScale) && requestedGeometryScale > 0) {
    widths = weights.map((weight) => Math.max(floor, weight * geometryScale));
  } else {
    const calibrationCount = entries.length > 1 ? entries.length - 1 : 1;
    const calibrationWeights = weights.slice(0, calibrationCount);
    const calibrationWidths = allocatePackedWidths({
      weights: calibrationWeights,
      bodySpan: targetBodyWidth * calibrationCount,
      minimumWidth: floor,
    });
    geometryScale = widthScaleFromAllocation({
      weights: calibrationWeights,
      widths: calibrationWidths,
      minimumWidth: floor,
    });
    widths = entries.length > 1
      ? [
          ...calibrationWidths,
          Math.max(floor, weights[weights.length - 1] * geometryScale),
        ]
      : calibrationWidths;
  }

  let cursor = 0;
  const items = entries.map((entry, index) => {
    const bodyWidth = widths[index];
    const bodyLeft = cursor;
    const bodyRight = bodyLeft + bodyWidth;
    cursor = bodyRight + resolvedGap;
    return {
      ...entry,
      x: bodyLeft + (bodyWidth / 2),
      left: bodyLeft,
      right: bodyRight,
      width: bodyWidth,
    };
  });

  return {
    items,
    gapPx: resolvedGap,
    left: 0,
    right: items[items.length - 1]?.right || 0,
    barSpacing: spacing,
    geometryScale,
  };
};

export const translateStableVolumeCandleGeometry = ({
  layout,
  offset,
  viewportLeft = 0,
  viewportRight,
}) => {
  const translation = Number(offset);
  const left = Number(viewportLeft);
  const right = Number(viewportRight);
  if (
    !layout ||
    !Number.isFinite(translation) ||
    !Number.isFinite(left) ||
    !Number.isFinite(right) ||
    right <= left
  ) {
    return {
      items: [],
      gapPx: 0,
      left: Number.isFinite(left) ? left : 0,
      right: Number.isFinite(right) ? right : 0,
      panOffset: Number.isFinite(translation) ? translation : 0,
      barSpacing: Number(layout?.barSpacing) || 0,
      geometryScale: Number(layout?.geometryScale) || 0,
    };
  }

  const edgeBuffer = Math.max(Number(layout.gapPx) || 0, 1);
  const sourceItems = Array.isArray(layout.items) ? layout.items : [];
  const firstVisibleRight = left - edgeBuffer - translation;
  let low = 0;
  let high = sourceItems.length;
  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    if (sourceItems[middle].right < firstVisibleRight) low = middle + 1;
    else high = middle;
  }

  const items = [];
  for (let index = low; index < sourceItems.length; index += 1) {
    const item = sourceItems[index];
    const translatedLeft = item.left + translation;
    if (translatedLeft > right + edgeBuffer) break;
    items.push({
      ...item,
      x: item.x + translation,
      left: translatedLeft,
      right: item.right + translation,
    });
  }

  return {
    items,
    gapPx: Number(layout.gapPx) || 0,
    left,
    right,
    panOffset: translation,
    barSpacing: Number(layout.barSpacing) || 0,
    geometryScale: Number(layout.geometryScale) || 0,
  };
};

export const findPackedVolumeCandle = (layout, x) => {
  const coordinate = Number(x);
  const items = Array.isArray(layout?.items) ? layout.items : [];
  if (!Number.isFinite(coordinate) || items.length === 0) return null;

  let closest = null;
  let closestDistance = Number.POSITIVE_INFINITY;
  items.forEach((item) => {
    if (coordinate >= item.left && coordinate <= item.right) {
      closest = item;
      closestDistance = 0;
      return;
    }
    if (closestDistance === 0) return;
    const distance = Math.min(
      Math.abs(coordinate - item.left),
      Math.abs(coordinate - item.right),
    );
    if (distance < closestDistance) {
      closest = item;
      closestDistance = distance;
    }
  });
  return closest;
};

export const summarizePackedVolumeCandleGeometry = (layout) => {
  const items = Array.isArray(layout?.items) ? layout.items : [];
  if (items.length === 0) {
    return {
      count: 0,
      gapMin: null,
      gapMax: null,
      widthMin: null,
      widthMax: null,
      overlapCount: 0,
    };
  }

  const gaps = items.slice(0, -1).map(
    (item, index) => items[index + 1].left - item.right,
  );
  const widths = items.map((item) => item.width);
  return {
    count: items.length,
    gapMin: gaps.length > 0 ? Math.min(...gaps) : 0,
    gapMax: gaps.length > 0 ? Math.max(...gaps) : 0,
    widthMin: Math.min(...widths),
    widthMax: Math.max(...widths),
    overlapCount: gaps.filter((gap) => gap < -0.001).length,
  };
};

export const buildVolumeCandleOverlayConfig = ({
  candleData,
  chartViewType,
  identity,
  palette,
  scale,
}) => {
  const enabled = chartViewType === VOLUME_CANDLE_VIEW;
  if (!enabled) {
    return { enabled: false, candles: [], referenceVolume: null };
  }

  const candles = Array.isArray(candleData) ? candleData : [];
  return {
    enabled: true,
    candles,
    identity,
    referenceVolume: volumeCandleReference(candles),
    scale,
    upColor: palette?.up || "#21c58e",
    downColor: palette?.down || "#ef5f6b",
    gridColor: palette?.grid || "rgba(148, 163, 184, 0.12)",
    textColor: palette?.text || "#cbd5e1",
    backgroundColor: palette?.background || "#0d1117",
    borderColor: palette?.border || "rgba(148, 163, 184, 0.24)",
  };
};

const niceTimeGuideStep = (rawStep) => {
  const normalized = Math.max(Number(rawStep) || 1, 1);
  const magnitude = 10 ** Math.floor(Math.log10(normalized));
  const fraction = normalized / magnitude;
  let niceFraction = 10;
  if (fraction <= 1) niceFraction = 1;
  else if (fraction <= 2) niceFraction = 2;
  else if (fraction <= 5) niceFraction = 5;
  return Math.max(Math.round(niceFraction * magnitude), 1);
};

const selectTimeGuideItems = (items, plotWidth, barSpacing) => {
  if (!Array.isArray(items) || items.length === 0) return [];
  const step = niceTimeGuideStep(180 / Math.max(Number(barSpacing) || 1, 1));
  const selected = items.filter((item) => (
    Number.isInteger(item.sourceIndex) &&
    item.sourceIndex % step === 0 &&
    item.x >= 0 &&
    item.x <= plotWidth
  ));
  if (selected.length > 0) return selected.slice(0, 8);

  const centered = items
    .filter((item) => item.x >= 0 && item.x <= plotWidth)
    .sort((left, right) => (
      Math.abs(left.x - (plotWidth / 2)) -
      Math.abs(right.x - (plotWidth / 2))
    ));
  return centered.length > 0 ? [centered[0]] : [];
};

const drawPackedTimeGuides = ({
  context,
  layout,
  overlay,
  plotWidth,
  plotHeight,
  axisHeight,
}) => {
  const guides = selectTimeGuideItems(
    layout.items,
    plotWidth,
    layout.barSpacing,
  );
  if (guides.length === 0) return;

  context.save();
  context.strokeStyle = overlay.gridColor;
  context.lineWidth = 1;
  guides.forEach((item) => {
    const alignedX = Math.round(item.x) + 0.5;
    context.beginPath();
    context.moveTo(alignedX, 0);
    context.lineTo(alignedX, plotHeight);
    context.stroke();
  });
  context.restore();

  if (axisHeight <= 0) return;
  context.save();
  context.fillStyle = overlay.backgroundColor;
  context.fillRect(0, plotHeight, plotWidth, axisHeight);
  context.strokeStyle = overlay.borderColor;
  context.lineWidth = 1;
  context.beginPath();
  context.moveTo(0, Math.round(plotHeight) + 0.5);
  context.lineTo(plotWidth, Math.round(plotHeight) + 0.5);
  context.stroke();
  context.fillStyle = overlay.textColor;
  context.font = "600 12px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  context.textAlign = "center";
  context.textBaseline = "middle";
  guides.forEach((item) => {
    const label = formatTickMark(item.candle?.time, overlay.scale);
    if (!label) return;
    const metrics = context.measureText(label);
    const halfWidth = (metrics?.width || 0) / 2;
    const x = Math.max(halfWidth + 3, Math.min(item.x, plotWidth - halfWidth - 3));
    context.fillText(label, x, plotHeight + (axisHeight / 2));
  });
  context.restore();
};

const resolveNativeBarSpacing = ({ timeScale, plotWidth }) => {
  const firstCoordinate = timeScale?.logicalToCoordinate?.(0);
  const secondCoordinate = timeScale?.logicalToCoordinate?.(1);
  const coordinateSpacing = Math.abs(secondCoordinate - firstCoordinate);
  if (Number.isFinite(coordinateSpacing) && coordinateSpacing > 0) {
    return coordinateSpacing;
  }

  const visibleRange = timeScale?.getVisibleLogicalRange?.();
  const visibleSpan = Number(visibleRange?.to) - Number(visibleRange?.from);
  if (Number.isFinite(visibleSpan) && visibleSpan > 0 && plotWidth > 0) {
    return plotWidth / visibleSpan;
  }

  const configuredSpacing = Number(timeScale?.options?.()?.barSpacing);
  return Number.isFinite(configuredSpacing) && configuredSpacing > 0
    ? configuredSpacing
    : Math.max(plotWidth / 80, 1);
};

const stableVolumeCandleLayoutCache = new WeakMap();
const volumeCandleSourceCache = new WeakMap();

const volumeCandleSourceEntries = (overlay) => {
  const cached = volumeCandleSourceCache.get(overlay);
  if (cached) return cached;

  const entries = overlay.candles.map((candle, sourceIndex) => ({
    candle,
    sourceIndex,
  }));
  volumeCandleSourceCache.set(overlay, entries);
  return entries;
};

const visibleLogicalBounds = ({ timeScale, count }) => {
  const range = timeScale?.getVisibleLogicalRange?.();
  const from = Number(range?.from);
  const to = Number(range?.to);
  if (!Number.isFinite(from) || !Number.isFinite(to) || to < from) {
    return { start: 0, end: count };
  }

  return {
    start: Math.max(Math.floor(from) - 2, 0),
    end: Math.min(Math.ceil(to) + 3, count),
  };
};

const calibrateStableLayout = ({
  chart,
  timeScale,
  allCandles,
  barSpacing,
  plotWidth,
  referenceVolume,
  identity,
  store = true,
}) => {
  const { start, end } = visibleLogicalBounds({
    timeScale,
    count: allCandles.length,
  });
  const visibleCandles = allCandles
    .slice(start, end)
    .map((entry) => ({
      ...entry,
      sourceX: timeScale.logicalToCoordinate?.(entry.sourceIndex) ??
        timeScale.timeToCoordinate(entry.candle?.time),
    }))
    .filter((entry) => (
      Number.isFinite(entry.sourceX) &&
      entry.sourceX >= -barSpacing &&
      entry.sourceX <= plotWidth + barSpacing
    ));
  if (visibleCandles.length === 0) return null;

  const left = Math.max(0, visibleCandles[0].sourceX - (barSpacing / 2));
  const right = Math.min(
    plotWidth,
    visibleCandles[visibleCandles.length - 1].sourceX + (barSpacing / 2),
  );
  const initialLayout = buildPackedVolumeCandleGeometry({
    candles: visibleCandles,
    left,
    right,
    referenceVolume,
  });
  const weights = visibleCandles.map((entry) => volumeCandleWidthRatio({
    volume: entry.candle?.volume,
    referenceVolume,
  }));
  const geometryScale = widthScaleFromAllocation({
    weights,
    widths: initialLayout.items.map((item) => item.width),
    minimumWidth: VOLUME_CANDLE_MIN_BODY_WIDTH_PX,
  });
  const anchor = visibleCandles[0];
  const anchorNativeCoordinate = timeScale.logicalToCoordinate?.(
    anchor.sourceIndex,
  ) ?? anchor.sourceX;
  if (!Number.isFinite(geometryScale) || geometryScale <= 0 ||
      !Number.isFinite(anchorNativeCoordinate)) {
    return null;
  }

  const normalizedReference = finitePositiveVolume(referenceVolume);
  const cache = {
    scale: geometryScale,
    pixelsPerVolume: normalizedReference == null
      ? null
      : geometryScale / normalizedReference,
    referenceVolume: normalizedReference,
    identity,
    barSpacing,
    firstTime: allCandles[0]?.candle?.time,
    lastTime: allCandles[allCandles.length - 1]?.candle?.time,
    lastVolume: finitePositiveVolume(
      allCandles[allCandles.length - 1]?.candle?.volume,
    ),
    candleCount: allCandles.length,
    anchorIndex: anchor.sourceIndex,
    anchorNativeCoordinate,
    anchorLayoutLeft: initialLayout.items[0]?.left || 0,
  };
  if (store) stableVolumeCandleLayoutCache.set(chart, cache);
  return cache;
};

const stableLayoutCacheMatches = ({
  cache,
  allCandles,
  barSpacing,
  identity,
  referenceVolume,
}) => (
  cache &&
  cache.identity === identity &&
  cache.firstTime === allCandles[0]?.candle?.time &&
  cache.lastTime === allCandles[allCandles.length - 1]?.candle?.time &&
  cache.candleCount === allCandles.length &&
  cache.referenceVolume === finitePositiveVolume(referenceVolume) &&
  Math.abs(cache.barSpacing - barSpacing) <= 0.001
);

export const volumeCandlePriceRangeForLayout = (layout) => {
  const items = Array.isArray(layout?.items) ? layout.items : [];
  const viewportLeft = Number(layout?.left);
  const viewportRight = Number(layout?.right);
  const hasViewport = Number.isFinite(viewportLeft) &&
    Number.isFinite(viewportRight) &&
    viewportRight > viewportLeft;
  const prices = items
    .filter((item) => (
      !hasViewport ||
      (Number(item?.right) >= viewportLeft &&
        Number(item?.left) <= viewportRight)
    ))
    .flatMap((item) => [
      item?.candle?.low,
      item?.candle?.high,
      item?.candle?.open,
      item?.candle?.close,
    ])
    .map(Number)
    .filter(Number.isFinite);
  if (prices.length === 0) return null;

  return {
    minValue: Math.min(...prices),
    maxValue: Math.max(...prices),
  };
};

export const extendAutoscaleInfoWithVolumeCandleLayout = ({
  autoscaleInfo,
  layout,
}) => {
  const packedRange = volumeCandlePriceRangeForLayout(layout);
  if (!packedRange) return autoscaleInfo;

  const nativeRange = autoscaleInfo?.priceRange;
  const nativeMin = Number(nativeRange?.minValue);
  const nativeMax = Number(nativeRange?.maxValue);
  return {
    ...(autoscaleInfo || {}),
    priceRange: {
      minValue: Number.isFinite(nativeMin)
        ? Math.min(nativeMin, packedRange.minValue)
        : packedRange.minValue,
      maxValue: Number.isFinite(nativeMax)
        ? Math.max(nativeMax, packedRange.maxValue)
        : packedRange.maxValue,
    },
  };
};

export const resolveVolumeCandleLayout = ({
  chart,
  overlay,
  width,
  pixelRatio = 1,
  storeCalibration = true,
}) => {
  if (!overlay?.enabled || !chart) return null;

  const timeScale = chart.timeScale?.();
  if (!timeScale?.timeToCoordinate) return null;

  const suppliedWidth = Number(width);
  const timeScaleWidth = Number(timeScale.width?.());
  const fallbackWidth = Number.isFinite(suppliedWidth) && suppliedWidth > 0
    ? suppliedWidth
    : timeScaleWidth;
  const plotWidth = Math.max(
    Math.min(
      Number.isFinite(timeScaleWidth) && timeScaleWidth > 0
        ? timeScaleWidth
        : fallbackWidth,
      fallbackWidth,
    ),
    0,
  );
  if (!Number.isFinite(plotWidth) || plotWidth <= 0) return null;

  const allCandles = volumeCandleSourceEntries(overlay);
  if (allCandles.length === 0) return null;

  const barSpacing = resolveNativeBarSpacing({ timeScale, plotWidth });
  let cache = stableVolumeCandleLayoutCache.get(chart);
  if (!stableLayoutCacheMatches({
    cache,
    allCandles,
    barSpacing,
    identity: overlay.identity,
    referenceVolume: overlay.referenceVolume,
  })) {
    cache = calibrateStableLayout({
      chart,
      timeScale,
      allCandles,
      barSpacing,
      plotWidth,
      referenceVolume: overlay.referenceVolume,
      identity: overlay.identity,
      store: storeCalibration,
    });
  } else {
    // A live bar may grow without changing the calibrated history. Rebuild
    // its aligned body against the existing scale instead of reflowing every
    // completed candle.
    const lastVolume = finitePositiveVolume(
      allCandles[allCandles.length - 1]?.candle?.volume,
    );
    if (cache.lastVolume !== lastVolume) {
      if (!storeCalibration) cache = { ...cache };
      cache.lastVolume = lastVolume;
      cache.alignedStableLayout = null;
    }
  }
  if (!cache) return null;

  const normalizedReference = finitePositiveVolume(overlay.referenceVolume);
  const geometryScale = cache.pixelsPerVolume != null &&
    normalizedReference != null
    ? cache.pixelsPerVolume * normalizedReference
    : cache.scale;
  const normalizedRatio = normalizedPixelRatio(pixelRatio);
  const canReuseAlignedLayout = (
    cache.alignedStableLayout &&
    cache.alignedGeometryScale === geometryScale &&
    cache.alignedPixelRatio === normalizedRatio
  );
  let alignedStableLayout = cache.alignedStableLayout;
  if (!canReuseAlignedLayout) {
    const stableLayout = buildStableVolumeCandleGeometry({
      candles: allCandles,
      barSpacing,
      referenceVolume: overlay.referenceVolume,
      geometryScale,
    });
    alignedStableLayout = alignVolumeCandleLayoutToDevicePixels({
      layout: stableLayout,
      pixelRatio: normalizedRatio,
    });
    if (storeCalibration) {
      cache.alignedStableLayout = alignedStableLayout;
      cache.alignedGeometryScale = geometryScale;
      cache.alignedPixelRatio = normalizedRatio;
    }
  }
  const anchorCoordinate = timeScale.logicalToCoordinate?.(
    cache.anchorIndex,
  ) ?? timeScale.timeToCoordinate(
    allCandles[cache.anchorIndex]?.candle?.time,
  );
  const anchorItem = alignedStableLayout.items[cache.anchorIndex];
  if (!Number.isFinite(anchorCoordinate) || !anchorItem) return null;

  const layout = translateStableVolumeCandleGeometry({
    layout: alignedStableLayout,
    offset: alignVolumeCandleTranslationToDevicePixels({
      offset:
        cache.anchorLayoutLeft - anchorItem.left +
        anchorCoordinate - cache.anchorNativeCoordinate,
      pixelRatio,
    }),
    viewportLeft: 0,
    viewportRight: plotWidth,
  });
  if (layout.items.length === 0) return null;

  layout.anchorIndex = cache.anchorIndex;
  layout.anchorX = anchorItem.x + layout.panOffset;
  layout.anchorWidth = anchorItem.width;
  layout.plotWidth = plotWidth;
  layout.priceRange = volumeCandlePriceRangeForLayout(layout);
  return layout;
};

export const drawVolumeCandles = ({
  chart,
  series,
  overlay,
  context,
  width,
  height,
}) => {
  if (!overlay?.enabled || !chart || !series || !context) return null;

  const timeScale = chart.timeScale?.();
  if (!timeScale?.timeToCoordinate || !series.priceToCoordinate) return null;

  const plotWidth = Math.max(
    Math.min(Number(timeScale.width?.()) || width, width),
    0,
  );
  const axisHeight = Math.max(
    Math.min(Number(timeScale.height?.()) || 0, height),
    0,
  );
  const plotHeight = Math.max(height - axisHeight, 0);
  const transform = context.getTransform?.();
  const pixelRatioX = normalizedPixelRatio(transform?.a);
  const pixelRatioY = normalizedPixelRatio(transform?.d);
  const layout = resolveVolumeCandleLayout({
    chart,
    overlay,
    width,
    pixelRatio: pixelRatioX,
  });
  if (!layout) return null;

  drawPackedTimeGuides({
    context,
    layout,
    overlay,
    plotWidth,
    plotHeight,
    axisHeight,
  });

  context.save();
  context.beginPath();
  context.rect(0, 0, plotWidth, plotHeight);
  context.clip();
  let priceCoordinateMin = Number.POSITIVE_INFINITY;
  let priceCoordinateMax = Number.NEGATIVE_INFINITY;
  let priceOutOfBoundsCount = 0;
  layout.items.forEach((item) => {
    if (item.right < 0 || item.left > plotWidth) return;

    const { candle, x, width: bodyWidth } = item;
    const openY = series.priceToCoordinate(candle?.open);
    const highY = series.priceToCoordinate(candle?.high);
    const lowY = series.priceToCoordinate(candle?.low);
    const closeY = series.priceToCoordinate(candle?.close);
    if (![x, openY, highY, lowY, closeY].every(Number.isFinite)) return;

    const candleTop = Math.min(openY, highY, lowY, closeY);
    const candleBottom = Math.max(openY, highY, lowY, closeY);
    priceCoordinateMin = Math.min(priceCoordinateMin, candleTop);
    priceCoordinateMax = Math.max(priceCoordinateMax, candleBottom);
    if (candleTop < -0.001 || candleBottom > plotHeight + 0.001) {
      priceOutOfBoundsCount += 1;
    }

    const color = candle.close >= candle.open
      ? overlay.upColor
      : overlay.downColor;
    const bodyTop = Math.min(openY, closeY);
    const bodyHeight = Math.max(Math.abs(closeY - openY), 1);

    context.fillStyle = color;
    const wickRect = alignVolumeCandleWickToDevicePixels({
      x,
      top: highY,
      bottom: lowY,
      lineWidth: 1,
      pixelRatioX,
      pixelRatioY,
    });
    context.fillRect(
      wickRect.left,
      wickRect.top,
      wickRect.width,
      wickRect.height,
    );
    const bodyRect = alignVolumeCandleRectToDevicePixels({
      left: item.left,
      right: item.left + bodyWidth,
      top: bodyTop,
      bottom: bodyTop + bodyHeight,
      pixelRatioX,
      pixelRatioY,
    });
    context.fillRect(
      bodyRect.left,
      bodyRect.top,
      bodyRect.width,
      bodyRect.height,
    );
  });
  context.restore();

  layout.plotHeight = plotHeight;
  layout.priceCoordinateMin = Number.isFinite(priceCoordinateMin)
    ? priceCoordinateMin
    : null;
  layout.priceCoordinateMax = Number.isFinite(priceCoordinateMax)
    ? priceCoordinateMax
    : null;
  layout.priceOutOfBoundsCount = priceOutOfBoundsCount;

  return layout;
};
